/* eslint-disable you-dont-need-lodash-underscore/get */
/* eslint-disable unicorn/no-nested-ternary */
/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
  Anchor, Badge, dlsColorSuccessBg, Heading, Label, Tooltip,
} from '@americanexpress/dls-react';
import { convertDateTimeStr } from '../../legacy/utils/TimeZoneUtil';
import { FxFieldLabelTooltip } from './FxFieldLabelTooltip';
import { defaultHeadingProps } from './previewDefaults';
import styleObj from '../../legacy/styles/EnhancedDataTable/EnhanceTableStyles';
import { FxModal } from '../FxModals';

export const FxPreviewFieldsContent = ({
  schema,
  section,
  component,
  headingProps: hp,
  setMissingInfo,
}) => {
  const {
    id,
    type = 'object',
    heading,
    hasHeadingContainer = false,
    columns = 4,
    fields = [],
    subComponents = [],
    subcomponentSeparated = false,
    subcomponentSeparatorColor,
    subComponentWrapClass,
    subComponentHeadingProps: schp,
    componentWrapClass,
    status,
    hasBorder = false,
    isFullHeight = false,
  } = component;

  const { id: schemaId } = schema || {};

  const [dataRows, setDataRows] = useState([]);
  // initialize to null to avoid unnecessary missing info message
  const [sectionToConsider, setSectionToConsider] = useState(null);
  const [hasAllInfoMissing, setHasAllInfoMissing] = useState(false);
  const [showModalTable, setShowModalTable] = useState({
    show: false,
    row: null,
  });

  // update sectionToConsider as part of useEffect to avoid infinite re-renders
  useEffect(() => {
    // the last block in next line not covering in the jest, need to check
    /* istanbul ignore next */
    const stc = component?.id
      ? _.get(section, id) || {}
      : section || {};
    setSectionToConsider(stc);
  }, [section]);

  // check if the value is missing for the field
  const isValueMissing = (data, index) => {
    const value = type === 'list'
      ? typeof sectionToConsider?.[index]?.[data.id] === 'number' ? sectionToConsider?.[index]?.[data.id].toString() : sectionToConsider?.[index]?.[data.id] || '__'
      : typeof _.get(sectionToConsider, data.id) === 'number' ? _.get(sectionToConsider, data.id)
        .toString() : _.get(sectionToConsider, data.id) || '__';
    if (_.isBoolean(data?.checkMissingValue)) {
      return data.checkMissingValue ? [data?.default, '__'].includes(value) : false;
    }
    return [typeof data?.default === 'number' ? data?.default.toString() : data?.default, '__'].includes(value);
  };

  // number of fields missing in the section
  let noOfInfoMissing = 0;
  // number of mandatory fields missing in the section
  let noOfMandatoryInfoMissing = 0;

  useEffect(() => {
    if (!sectionToConsider || fields.length === 0) return;

    // Determine the columns to be managed in a row and map the fields accordingly
    // also update the missing info based on the fields
    const dr = fields.map((field, idx) => {
      // update the number of missing fields
      if (isValueMissing(field, idx)) {
        noOfInfoMissing += 1;
        // update the number of missing mandatory fields
        noOfMandatoryInfoMissing = field.mandatory ? noOfMandatoryInfoMissing + 1 : noOfMandatoryInfoMissing;
      }

      // only update the missing info if it is the last field in the section to avoid continuous loops
      // only update these if the setMissingInfo is provided.
      // This allows to display field level warning even if all the fields are missing
      if (idx === fields.length - 1 && setMissingInfo && schemaId) {
        if (fields.length === noOfInfoMissing && noOfInfoMissing > 0) {
          // if ALL the fields are missing, set the flag to all
          setHasAllInfoMissing(true);
          setMissingInfo('all');
        } else if (noOfMandatoryInfoMissing > 0) {
          // if some mandatory fields are missing, set the flag to some
          setMissingInfo('some');
          setHasAllInfoMissing(false);
        } else {
          setMissingInfo('none');
          setHasAllInfoMissing(false);
        }
      }
      // return the fields based on the columns
      return fields.slice(idx * columns, idx * columns + columns);
    });
    setDataRows(dr);
  }, [sectionToConsider, fields]);

  // Format the value based on provided props
  const renderValue = (index, data) => {
    let value = type === 'list'
      ? typeof sectionToConsider?.[index]?.[data.id] === 'number' ? sectionToConsider?.[index]?.[data.id].toString() : sectionToConsider?.[index]?.[data.id] || '__'
      : typeof _.get(sectionToConsider, data.id) === 'number' ? _.get(sectionToConsider, data.id)
        .toString() : _.get(sectionToConsider, data.id) || '__';
    const {
      isDate,
      timezone,
      includeTime,
      includeTz,
      dFormat,
      tFormat,
      href,
      onAnchorClick,
      delimiter,
      showModalOnClick,
      fieldComponent: FieldComponent,
      detailsDataModal,
      mutiValueProp,
    } = data;

    const tableConditionFunc = showModalOnClick;
    let showModal = false;
    if (tableConditionFunc !== undefined) {
      showModal = tableConditionFunc(value, sectionToConsider);
    }

    if (FieldComponent
      && typeof delimiter === 'undefined'
      && showModal === false) {
      return (
        <p>
          <FieldComponent props={value} />
        </p>
      );
    }

    // format the string as date if required
    if (isDate && timezone) {
      const parsedDate = convertDateTimeStr(timezone, value, includeTime, includeTz, dFormat, tFormat);
      value = parsedDate === 'Invalid date' ? '__' : parsedDate;
    }

    // format the value as link if required
    value = href && onAnchorClick ? (
      <Anchor id={`${index}-anchor`} href={href} onClick={onAnchorClick}>
        {value}
      </Anchor>
      )
      : value;

    if (mutiValueProp && mutiValueProp?.count > 1) {
      return (
        <>
          <span style={{ display: 'inline-block', verticalAlign: 'middle', ...data.contentStyle }}>
            {value}
          </span>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <span
            className="plusMore"
            style={styleObj['.plusMore']}
            onClick={() => {
                  if (mutiValueProp?.onClick) {
                    mutiValueProp.onClick(value, sectionToConsider, section);
                  }
                }}
            title={typeof value === 'string' ? `${value}...` : ''}
          >
            {` +${mutiValueProp?.count - 1} `}
          </span>
        </>
      );
    }

    if (
        typeof delimiter !== 'undefined'
        && showModal !== true
        && typeof value !== 'undefined'
        && value !== null
    ) {
      const dataArr = value.split(delimiter);
      const firstElement = dataArr.shift();
      if (dataArr.length >= 1) {
        return (
          <>
            {firstElement}
            <Tooltip
              id="cell-data-tooltip"
              placement="bottom"
              info={true}
              triggerElement={(
                <div>
                  <span css={styleObj['.plusMore']}>
                    {` +${dataArr.length}`}
                  </span>
                </div>
                  )}
            >
              {dataArr.map((d) => d)
                    .join(', ')}
            </Tooltip>
          </>
        );
      }
    }
    if (
        typeof delimiter !== 'undefined'
        && showModal === true
        && typeof value !== 'undefined'
        && value !== null
        && detailsDataModal
    ) {
      const dataArr = value.split(delimiter);
      const {
             body: ModalBody = () => (<></>),
        // eslint-disable-next-line no-shadow
             heading = 'Details',
      } = detailsDataModal;
      const firstElement = dataArr.shift();
      if (dataArr.length >= 1) {
        return (
          <>
            {firstElement}
            {/* eslint-disable-next-line max-len */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <span
              css={styleObj['.plusMore']}
              onClick={() => {
                    setShowModalTable({
                      show: true,
                      row: sectionToConsider,
                    });
                  }}
            >
              {` +${dataArr.length}`}
            </span>
            { (showModalTable.show && showModalTable.row === sectionToConsider) && (
              <FxModal
                modalHeading={heading}
                modalBody={<ModalBody row={showModalTable.row} />}
                onClose={() => setShowModalTable({ show: false })}
                primaryButtonLabel="Close"
                onPrimaryButtonClick={() => setShowModalTable({ show: false })}
                justifyButtons="center"
                size="lg"
              />
              )}

          </>
        );
      }
    }
    return <p style={data.contentStyle}>{value}</p>;
  };

  let index = -1;

  const colClass = `col-xs-12 col-lg-${12 / columns}`;
  const componentContent = dataRows
    .filter((row) => row?.length > 0)
    .map((row) => (
      <div className="row stack text-wrap" key={row + index}>
        {row.map((data) => {
          index += 1;
          return (
            <div
              className={data.className || colClass}
              key={data.id}
            >
              <Label
                id={`${data.id}-Label`}
                htmlFor={data.id}
                className="label-2"
                theme={data.labelStyle}
                style={{ wordBreak: 'break-word' }}
              >
                {data.label}
                {/* if all the info is missing for the section,
                  do not display field level tooltip for missing value */}
                <FxFieldLabelTooltip
                  id={data.id}
                  field={data}
                  isValueMissing={!hasAllInfoMissing && isValueMissing(data, index)}
                  sectionToConsider={sectionToConsider}
                />
              </Label>
              <br />
              {renderValue(index, data)}
            </div>
          );
        })}
      </div>
    ));

  const renderSubComponents = () => {
    const subComponentHeadingProps = { ...defaultHeadingProps, ...schp };

    const length = subComponents.length - 1;
    return subComponents.map((subComponent, i) => (
      subComponent.parentComponent
        ? subComponent.parentComponent // display custom component from parent instead of usual subcomponent
        : (
          <>
            <subComponent.componentName
              key={subComponent.heading}
              headingProps={subComponentHeadingProps}
              section={sectionToConsider}
              component={subComponent}
              setMissingInfo={setMissingInfo}
              toolTipMetaData={subComponent.toolTipMetaData}
            />
            {/* add horizontal line if subcomponents are separated, and it isn't last component */}
            {subcomponentSeparated && length !== i
              && (
                <hr
                  style={subcomponentSeparatorColor && { borderColor: subcomponentSeparatorColor }}
                  className="margin-2-b"
                />
              )}
          </>
        )
    ));
  };

  const renderHeading = () => {
    const {
      value,
      label,
      theme = dlsColorSuccessBg,
    } = status || {};

    const headingProps = { ...defaultHeadingProps, ...hp };

    const {
      level = 5,
      headingStyle = 'heading2',
      theme: headerTheme,
      wrapClass,
      tooltipMetaData,
    } = headingProps;

    return (
      <div
        className={`flex flex-justify-between ${value || hasHeadingContainer ? ' border-b dls-gray-01-bg pad-2-lr pad-1-tb' : ''} ${wrapClass || ''}`}
      >
        <div className="flex flex-align-center">
          <Heading
            level={level}
            headingStyle={headingStyle}
            theme={headerTheme}
          >
            {heading}
          </Heading>
          {/* Tooltip to be displayed at this level */}
          <FxFieldLabelTooltip
            id={id}
            field={tooltipMetaData}
            sectionToConsider={sectionToConsider}
          />
        </div>
        {/* Display status if value is provided */}
        {value && (
          <div className="flex flex-align-center">
            <Badge type="status" label="status" theme={theme} />
            <p>{label ? `${label} : ` : ''}
              {value}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id={id} className={`margin-0-b ${hasBorder && 'border'} ${isFullHeight && 'height-full'}`}>
      {heading && renderHeading()}

      {componentWrapClass
        ? (
          <div className={componentWrapClass}>
            {componentContent}
          </div>
        )
        : componentContent}

      {subComponentWrapClass
        ? (
          <div className={subComponentWrapClass}>
            {renderSubComponents()}
          </div>
        )
        : renderSubComponents()}
    </div>
  );
};

FxPreviewFieldsContent.propTypes = {
  schema: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  component: PropTypes.shape({
    id: PropTypes.string,
    heading: PropTypes.string,
    hasHeadingContainer: PropTypes.bool, // flag to indicate if the heading should be displayed in a container
    componentWrapClass: PropTypes.string,
    subComponentWrapClass: PropTypes.string,
    subcomponentSeparated: PropTypes.bool,
    subcomponentSeparatorColor: PropTypes.string,
    columns: PropTypes.number,
    type: PropTypes.string,
    fields: PropTypes.arrayOf({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      className: PropTypes.string,
      labelStyle: PropTypes.shape({}),
      contentStyle: PropTypes.shape({}),
    }).isRequired,
    subComponents: PropTypes.arrayOf(PropTypes.shape({})),
    subComponentHeadingProps: PropTypes.shape({
      level: PropTypes.number,
      style: PropTypes.string,
      theme: PropTypes.string,
      wrapClass: PropTypes.string,
      tooltipMetaData: PropTypes.shape({}),
    }),
    status: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      theme: PropTypes.string,
    }),
    hasBorder: PropTypes.bool,
    isFullHeight: PropTypes.bool,
  }),
  section: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
  headingProps: PropTypes.shape({
    level: PropTypes.number,
    headingStyle: PropTypes.string,
  }).isRequired,
  setMissingInfo: PropTypes.func,
};

FxPreviewFieldsContent.defaultProps = {
  section: {},
};
